import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteTicketRiskWatch, putTicketRiskWatch } from '~/services/SupportService';

import type { TicketRiskReport, TicketRiskWatchResult } from '~/types/ticketRisk';

import { ticketRiskQueryKeys } from './useTicketRiskReport';

/**
 * Writes a ticket's new watcher into a cached report. The report carries the full score
 * breakdown for every ticket, so patching one row beats refetching it after each click.
 */
export function patchReportWatch(
  report: TicketRiskReport | undefined,
  result: TicketRiskWatchResult,
): TicketRiskReport | undefined {
  if (!report) {
    return report;
  }

  return {
    ...report,
    tickets: report.tickets.map((ticket) =>
      ticket.ticketId === result.ticketId ? { ...ticket, watch: result.watch ?? null } : ticket,
    ),
  };
}

function useApplyWatchResult() {
  const queryClient = useQueryClient();

  return (result: TicketRiskWatchResult) =>
    queryClient.setQueriesData<TicketRiskReport>({ queryKey: ticketRiskQueryKeys.reports() }, (report) =>
      patchReportWatch(report, result),
    );
}

/** Become the ticket's watcher — also how a takeover works; svc-support releases the previous watcher. */
export function useWatchTicketRisk() {
  const applyResult = useApplyWatchResult();

  return useMutation({
    mutationFn: ({ ticketId }: { ticketId: string }) => putTicketRiskWatch(ticketId),
    onSuccess: applyResult,
  });
}

export function useUnwatchTicketRisk() {
  const applyResult = useApplyWatchResult();

  return useMutation({
    mutationFn: ({ ticketId }: { ticketId: string }) => deleteTicketRiskWatch(ticketId),
    onSuccess: applyResult,
  });
}
